import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Table,
  Spinner,
  Modal,
  Form,
  Nav,
} from "react-bootstrap";
import { useParams, useHistory } from "react-router-dom";
import AssignmentService, {
  AssignmentSubmission,
} from "../../_services/AssignmentService";
import "./assignments.css";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "SUBMITTED", label: "Submitted" },
  { key: "IN_REVIEW", label: "In Review" },
  { key: "GRADED", label: "Graded" },
  { key: "RETURNED", label: "Returned" },
  { key: "DRAFT", label: "Drafts" },
];

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  DRAFT: { bg: "#f5f5f5", color: "#757575" },
  SUBMITTED: { bg: "#e3f2fd", color: "#1565c0" },
  IN_REVIEW: { bg: "#fff3e0", color: "#e65100" },
  GRADED: { bg: "#e8f5e9", color: "#2e7d32" },
  RETURNED: { bg: "#fce4ec", color: "#c62828" },
  COMPLETED: { bg: "#e8f5e9", color: "#1b5e20" },
};

const AssignmentSubmissionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const assignmentId = Number(id);

  const [assignment, setAssignment] = useState<any>(null);
  const [submissions, setSubmissions] = useState<AssignmentSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  // Grade modal
  const [gradeModal, setGradeModal] = useState<AssignmentSubmission | null>(
    null
  );
  const [gradeScore, setGradeScore] = useState("");
  const [gradeFeedback, setGradeFeedback] = useState("");
  const [gradeComments, setGradeComments] = useState("");
  const [grading, setGrading] = useState(false);

  // Return modal
  const [returnModal, setReturnModal] = useState<AssignmentSubmission | null>(
    null
  );
  const [returnFeedback, setReturnFeedback] = useState("");
  const [returning, setReturning] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await AssignmentService.getSubmissions(assignmentId, {
        status: statusFilter || undefined,
      });
      setAssignment(res.data?.data?.assignment || null);
      setSubmissions(res.data?.data?.submissions || []);
    } catch (err) {
      console.error("Failed to load submissions", err);
    }
    setLoading(false);
  }, [assignmentId, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGrade = async () => {
    if (!gradeModal) return;
    setGrading(true);
    try {
      await AssignmentService.gradeSubmission(assignmentId, gradeModal.Id, {
        score: gradeScore ? parseFloat(gradeScore) : undefined,
        maxScore: assignment?.MaxScore
          ? parseFloat(assignment.MaxScore)
          : undefined,
        feedbackText: gradeFeedback || undefined,
        reviewerComments: gradeComments || undefined,
      });
      setGradeModal(null);
      loadData();
    } catch (err) {
      console.error("Grade failed", err);
    }
    setGrading(false);
  };

  const handleReturn = async () => {
    if (!returnModal) return;
    setReturning(true);
    try {
      await AssignmentService.returnSubmission(assignmentId, returnModal.Id, {
        feedbackText: returnFeedback || undefined,
      });
      setReturnModal(null);
      loadData();
    } catch (err) {
      console.error("Return failed", err);
    }
    setReturning(false);
  };

  const openGradeModal = (s: AssignmentSubmission) => {
    setGradeScore(s.Score || "");
    setGradeFeedback(s.FeedbackText || "");
    setGradeComments(s.ReviewerComments || "");
    setGradeModal(s);
  };

  const renderSubmissionContent = (s: AssignmentSubmission) => {
    const parts: React.ReactNode[] = [];

    if (s.SubmissionText) {
      parts.push(
        <div key="text" className="mb-2">
          <strong>Text Response:</strong>
          <div
            className="p-2 bg-light rounded mt-1"
            style={{ whiteSpace: "pre-wrap", maxHeight: 300, overflow: "auto" }}
          >
            {s.SubmissionText}
          </div>
        </div>
      );
    }

    if (s.SubmissionData) {
      const data =
        typeof s.SubmissionData === "string"
          ? JSON.parse(s.SubmissionData)
          : s.SubmissionData;

      if (data.files?.length) {
        parts.push(
          <div key="files" className="mb-2">
            <strong>Uploaded Files:</strong>
            <ul className="mt-1">
              {data.files.map((f: any, i: number) => (
                <li key={i}>
                  <a href={f.url} target="_blank" rel="noreferrer">
                    {f.name}
                  </a>{" "}
                  {f.size && (
                    <span className="text-muted">
                      ({(f.size / 1024 / 1024).toFixed(1)} MB)
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      }

      if (data.answers) {
        parts.push(
          <div key="answers" className="mb-2">
            <strong>Question Answers:</strong>
            {Object.entries(data.answers).map(([qId, ans]: [string, any]) => (
              <div key={qId} className="p-2 bg-light rounded mt-1 mb-1">
                <div className="text-muted small">Question {qId}</div>
                <div style={{ whiteSpace: "pre-wrap" }}>{ans.text}</div>
              </div>
            ))}
          </div>
        );
      }
    }

    return parts.length > 0 ? parts : <span className="text-muted">No content</span>;
  };

  return (
    <div className="assignments-page">
      {/* Header */}
      <div className="d-flex align-items-center gap-3 mb-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => history.push("/assignments")}
        >
          Back
        </Button>
        <h4 className="mb-0">
          {assignment?.Title || "Assignment"} — Submissions
        </h4>
      </div>

      {assignment && (
        <div className="mb-3 p-3 bg-light rounded">
          <div>
            <strong>Response Type:</strong> {assignment.ResponseType}
          </div>
          {assignment.MaxScore && (
            <div>
              <strong>Max Score:</strong> {assignment.MaxScore}
            </div>
          )}
        </div>
      )}

      {/* Status Tabs */}
      <Nav variant="tabs" className="mb-3">
        {STATUS_TABS.map((tab) => (
          <Nav.Item key={tab.key}>
            <Nav.Link
              active={statusFilter === tab.key}
              onClick={() => setStatusFilter(tab.key)}
              style={{ cursor: "pointer" }}
            >
              {tab.label}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-5 text-muted">
          No submissions found for this filter.
        </div>
      ) : (
        <Table hover responsive className="assignments-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Late</th>
              <th>Attempt</th>
              <th>Score</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {submissions.map((s) => {
              const style = STATUS_STYLES[s.Status] || STATUS_STYLES.DRAFT;
              return (
                <tr key={s.Id}>
                  <td>
                    <div>{s.StudentName || "—"}</div>
                    <div className="text-muted small">
                      {s.StudentUsername || ""}
                    </div>
                  </td>
                  <td>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 4,
                        fontSize: 12,
                        backgroundColor: style.bg,
                        color: style.color,
                      }}
                    >
                      {s.Status}
                    </span>
                  </td>
                  <td>
                    {s.SubmittedAt
                      ? new Date(s.SubmittedAt).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    {s.IsLate ? (
                      <span style={{ color: "#c62828" }}>Yes</span>
                    ) : (
                      "No"
                    )}
                  </td>
                  <td>{s.AttemptNumber || 1}</td>
                  <td>
                    {s.Score
                      ? `${s.Score}/${s.MaxScore || assignment?.MaxScore || "?"}`
                      : "—"}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => openGradeModal(s)}
                      >
                        Grade
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setReturnFeedback("");
                          setReturnModal(s);
                        }}
                      >
                        Return
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Grade Modal */}
      <Modal
        show={!!gradeModal}
        onHide={() => setGradeModal(null)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Grade Submission — {gradeModal?.StudentName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            {/* Left: Submission content */}
            <div className="col-md-7">
              <h6>Student Submission</h6>
              {gradeModal && renderSubmissionContent(gradeModal)}
            </div>

            {/* Right: Grading */}
            <div className="col-md-5">
              <h6>Grading</h6>
              <Form.Group className="mb-3">
                <Form.Label>
                  Score{" "}
                  {assignment?.MaxScore && (
                    <span className="text-muted">
                      (out of {assignment.MaxScore})
                    </span>
                  )}
                </Form.Label>
                <Form.Control
                  type="number"
                  value={gradeScore}
                  onChange={(e) => setGradeScore(e.target.value)}
                  placeholder="Score"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Feedback (visible to student)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={gradeFeedback}
                  onChange={(e) => setGradeFeedback(e.target.value)}
                  placeholder="Feedback for the student..."
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Internal Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={gradeComments}
                  onChange={(e) => setGradeComments(e.target.value)}
                  placeholder="Internal notes (not visible to student)..."
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setGradeModal(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleGrade} disabled={grading}>
            {grading ? <Spinner size="sm" /> : "Save Grade"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Return Modal */}
      <Modal show={!!returnModal} onHide={() => setReturnModal(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Return for Revision</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will return the submission to{" "}
            <strong>{returnModal?.StudentName}</strong> for revision.
          </p>
          <Form.Group>
            <Form.Label>Feedback</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={returnFeedback}
              onChange={(e) => setReturnFeedback(e.target.value)}
              placeholder="Tell the student what to improve..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReturnModal(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleReturn}
            disabled={returning}
          >
            {returning ? <Spinner size="sm" /> : "Return to Student"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignmentSubmissionsPage;
